import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/withErrorHandling";
import {
  CreatePushStationRequest,
  MAX_POLL_INTERVAL_S,
  MS_TO_KNOTS,
  PullProvider,
  PullTestResult,
  PushStationCredential,
  PushStationService,
  StationContact,
  UpdatePullRequest,
  ORGANIZATION_TYPES,
  STATION_TYPES,
} from "@/services/PushStationService";
import JsonMappingConfigurator from "@/components/wind/JsonMappingConfigurator";
import LocationPickerMap from "@/components/admin/LocationPickerMap";
import JsonTestStatus, { computeJsonTestStatus } from "@/components/wind/JsonTestStatus";
import {
  JsonPullMapping,
  browserTimeZone,
  cloneMapping,
  describeErrorCode,
  mappingKey,
  normalizeMapping,
  summarizeMapping,
  toEpochMs,
  validateMapping,
} from "@/utils/jsonMapping";

/**
 * Pull-station wizard (SRF-2870).
 *
 * Replaces the pull branch of the create modal and the pull-config section of the settings
 * view with one guided flow: pick the format, fill in only what that format needs, test the
 * URL against the real upstream, then the station identity, then review.
 *
 * Everything per-format — which fields exist, their labels, the URL placeholder, the
 * interval bounds and default — comes from the backend catalogue
 * (`GET /api/admin/push-stations/pull-providers`). There is deliberately no hard-coded
 * provider list here: the previous `PULL_PROVIDERS` constant had already gone stale against
 * the backend, which silently hid a usable format from ops.
 */

type WizardStep = "source" | "connection" | "station" | "review";

/**
 * Station BEFORE connection: the location must exist when the JSON configurator opens, so
 * its local-time default zone comes from the station's position rather than the operator's
 * browser (contract "Auto-guess → tz", Ali 2026-10-29).
 */
/** Debounce before the JSON mapping is re-tested after an edit. */
const JSON_AUTO_TEST_DELAY_MS = 600;

const CREATE_STEPS: WizardStep[] = ["source", "station", "connection", "review"];
/**
 * Edit is Connection -> Review only. The Station step used to be here too, but this wizard
 * writes ONLY the pull config (`PUT /{ref}/pull`); name/coordinates belong to the station
 * record and are edited in the settings dialog. Showing editable identity fields here made
 * those edits look saved when they were silently dropped on submit, so the step is gone
 * rather than made read-only-but-present. Review still shows the identity, read-only, from
 * the existing row.
 */
const EDIT_STEPS: WizardStep[] = ["connection", "review"];

const STEP_TITLES: Record<WizardStep, string> = {
  source: "Source",
  connection: "Connection",
  station: "Station",
  review: "Review",
};

export const inputCls =
  "w-full rounded bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:outline-none focus:border-accent";
const selectCls = inputCls;
const labelCls = "mb-1 block text-sm text-white75";

type ContactFields = {
  contactName: string;
  organization: string;
  organizationType: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  stationType: string;
  hardware: string;
  mastHeightM: string;
  spotId: string;
  notes: string;
};

const emptyContact: ContactFields = {
  contactName: "", organization: "", organizationType: "", address: "", email: "",
  phone: "", website: "", stationType: "", hardware: "", mastHeightM: "", spotId: "", notes: "",
};

const contactFromFields = (f: ContactFields): StationContact | undefined => {
  const contact: StationContact = {
    contactName: f.contactName.trim() || undefined,
    organization: f.organization.trim() || undefined,
    organizationType: f.organizationType || undefined,
    address: f.address.trim() || undefined,
    email: f.email.trim() || undefined,
    phone: f.phone.trim() || undefined,
    website: f.website.trim() || undefined,
    stationType: f.stationType || undefined,
    hardware: f.hardware.trim() || undefined,
    mastHeightM: f.mastHeightM.trim() ? Number(f.mastHeightM) : undefined,
    spotId: f.spotId.trim() ? Number(f.spotId) : undefined,
    notes: f.notes.trim() || undefined,
  };
  return Object.values(contact).some((v) => v !== undefined) ? contact : undefined;
};

const knots = (ms: number | null | undefined): string =>
  ms == null || !Number.isFinite(ms) ? "—" : `${(ms * MS_TO_KNOTS).toFixed(1)} kts`;

const degrees = (deg: number | null | undefined): string =>
  deg == null || !Number.isFinite(deg) ? "—" : `${Math.round(deg)}°`;

const observedAtText = (raw: number | string | null | undefined): string => {
  const ms = toEpochMs(raw);
  return ms == null ? "—" : new Date(ms).toISOString().replace("T", " ").slice(0, 19) + " UTC";
};

/**
 * Interval validation. The lower bound is per-format (a clientraw file that only updates
 * every 5 min gains nothing from a 60 s poll), the upper bound is the global backend cap.
 */
export const validateInterval = (
  raw: string,
  provider: Pick<PullProvider, "minIntervalS"> | null
): string | null => {
  const min = provider?.minIntervalS ?? 60;
  const trimmed = raw.trim();
  if (!trimmed) return `Interval is required — between ${min} and ${MAX_POLL_INTERVAL_S} seconds.`;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < min || n > MAX_POLL_INTERVAL_S) {
    return `Interval must be a whole number between ${min} and ${MAX_POLL_INTERVAL_S} seconds.`;
  }
  return null;
};

/** Per-format connection validation, driven entirely by the catalogue entry. */
export const validateConnection = (args: {
  provider: PullProvider | null;
  url: string;
  externalId: string;
  token: string;
  tokenConfigured: boolean;
  /** Edit mode has no Source step, so "choose a format first" is nonsense advice there. */
  isEdit?: boolean;
  /** The format the row already stores, shown when the catalogue no longer describes it. */
  storedProviderId?: string;
}): string | null => {
  const { provider, url, externalId, token, tokenConfigured, isEdit, storedProviderId } = args;
  if (!provider) {
    if (!isEdit) return "Choose a source format first.";
    // There is no format to choose from in edit mode — either the catalogue never
    // arrived, or it arrived without the format this station was configured with.
    return storedProviderId
      ? `The pull-format catalogue does not describe "${storedProviderId}" — it could not be loaded, or the backend no longer offers that format. Retry the catalogue or pick a current format.`
      : "The pull-format catalogue could not be loaded, so this station's format is unknown. Retry the catalogue before saving.";
  }
  if (!url.trim().startsWith("https://")) return "Poll URL must be https.";
  if (provider.needsExternalId && !externalId.trim()) {
    return `${provider.label} needs the ${provider.externalIdLabel ?? "provider's own station id"}.`;
  }
  // A station with neither a new token nor a stored one would 401 on every fetch, so the
  // "configured" flag is what makes an empty field legitimate in edit mode.
  if (provider.needsAuthToken && !token.trim() && !tokenConfigured) {
    return `${provider.label} needs the station owner's access token.`;
  }
  return null;
};

const StepHeader: React.FC<{ steps: WizardStep[]; current: WizardStep }> = ({ steps, current }) => (
  <ol className="flex flex-wrap items-center gap-2 text-xs" aria-label="Wizard steps">
    {steps.map((s, i) => {
      const active = s === current;
      const done = steps.indexOf(current) > i;
      return (
        <li
          key={s}
          aria-current={active ? "step" : undefined}
          className={`rounded px-2 py-1 ${
            active
              ? "bg-accent font-medium text-white"
              : done
                ? "bg-gray-700 text-green-300"
                : "bg-gray-700 text-white75"
          }`}
        >
          {i + 1}. {STEP_TITLES[s]}
        </li>
      );
    })}
  </ol>
);

/** Green/red panel rendering one test result. Advisory only — it never blocks saving. */
const TestResultPanel: React.FC<{ result: PullTestResult }> = ({ result }) => {
  if (!result.ok) {
    return (
      <div className="mt-3 rounded border border-red-700 bg-red-900/30 p-3 text-sm text-red-200">
        <p className="font-medium">Test failed</p>
        <p className="mt-1 break-words">
          {describeErrorCode(result.error) || "The upstream did not return usable data."}
        </p>
        <p className="mt-2 text-xs text-red-200/80">
          You can still save — an upstream can be down at registration time. Saving a failed
          test asks for a confirmation first.
        </p>
      </div>
    );
  }
  const s = result.sample;
  return (
    <div className="mt-3 rounded border border-green-700 bg-green-900/30 p-3 text-sm text-green-200">
      <p className="font-medium">Test succeeded — the URL parsed</p>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div>
          <dt className="inline text-green-200/70">Wind: </dt>
          <dd className="inline text-white">{knots(s?.windMs)}</dd>
        </div>
        <div>
          <dt className="inline text-green-200/70">Gust: </dt>
          <dd className="inline text-white">{knots(s?.gustMs)}</dd>
        </div>
        <div>
          <dt className="inline text-green-200/70">Direction: </dt>
          <dd className="inline text-white">{degrees(s?.dirDeg)}</dd>
        </div>
        <div>
          <dt className="inline text-green-200/70">Observed: </dt>
          <dd className="inline text-white">{observedAtText(s?.observedAt)}</dd>
        </div>
        {s?.lat != null && s?.lon != null && (
          <div className="col-span-2">
            <dt className="inline text-green-200/70">Position from the source: </dt>
            <dd className="inline text-white">
              {s.lat}, {s.lon}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

export type PullStationWizardProps = {
  /** Absent = create a new pull station; present = edit that station's pull config. */
  station?: PushStationCredential | null;
  onClose: () => void;
  /** Fired after a successful create or update so the caller can refresh + toast. */
  onSaved: (mode: "create" | "edit") => void;
};

const PullStationWizard: React.FC<PullStationWizardProps> = ({ station, onClose, onSaved }) => {
  const isEdit = Boolean(station);
  const existing = station?.contact ?? null;
  const steps = isEdit ? EDIT_STEPS : CREATE_STEPS;

  const [providers, setProviders] = useState<PullProvider[]>([]);
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  const [loadingCatalogue, setLoadingCatalogue] = useState(true);

  const [step, setStep] = useState<WizardStep>(isEdit ? "connection" : "source");
  const [providerId, setProviderId] = useState(existing?.pollProvider ?? "");
  const [url, setUrl] = useState(existing?.pollUrl ?? "");
  const [externalId, setExternalId] = useState(existing?.pollExternalId ?? "");
  // Always starts EMPTY: the stored token is never sent to the client, so there is nothing
  // to prefill. Empty on save means "keep what is stored".
  const [token, setToken] = useState("");
  const [intervalS, setIntervalS] = useState(
    existing?.pollIntervalS != null ? String(existing.pollIntervalS) : ""
  );
  const [intervalTouched, setIntervalTouched] = useState(existing?.pollIntervalS != null);

  // Station identity + ops metadata — CREATE ONLY. Edit has no Station step and this
  // wizard's edit path calls `updatePull`, which cannot write any of it; seeding these
  // from the existing row would only re-create the illusion that editing them saves.
  const [stationId, setStationId] = useState("");
  const [name, setName] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [contact, setContact] = useState<ContactFields>({ ...emptyContact });

  // JSON provider only: the field mapping (`pollOptions`). Edit mode starts from the stored one.
  const [mapping, setMapping] = useState<JsonPullMapping | null>(existing?.pollOptions ?? null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<PullTestResult | null>(null);
  /** `url|mapping` the current testResult was produced for (json provider gating). */
  const [testedKey, setTestedKey] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * A test is an async round-trip the operator can outrun: change the URL, or close the
   * dialog, while one is still in flight. Without these two guards a late response
   * reinstates a result for inputs that no longer exist — which also silently satisfies
   * the failed-test confirmation gate on save. Only the newest request may write state,
   * and only while the component is still mounted.
   */
  const testSeq = useRef(0);
  /** url|mapping of the json test in flight (if any) — the auto-test never duplicates it. */
  const inFlightKey = useRef<string | null>(null);
  /** The debounced json auto-test is scheduled but has not fired yet. */
  const [autoPending, setAutoPending] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const tokenConfigured = Boolean(existing?.pollAuthTokenConfigured);
  const provider = useMemo(
    () => providers.find((p) => p.id === providerId) ?? null,
    [providers, providerId]
  );
  const needsOptions = Boolean(provider?.needsOptions);
  const currentJsonKey = `${url.trim()}|${mappingKey(mapping)}`;
  /**
   * The key the UI shows NOW, readable from an async test callback. Each json test is
   * tagged with the key it was sent for; a response whose tag no longer matches (the url or
   * mapping changed while it was in flight) is dropped instead of landing on the new
   * inputs — the stale-response race from SRF-3138.
   */
  const currentKeyRef = useRef(currentJsonKey);
  currentKeyRef.current = currentJsonKey;
  /**
   * JSON formats are NOT advisory: a wrong mapping produces plausible-looking garbage
   * rather than no data, so save requires a passing server test for exactly this URL +
   * mapping (contract, Configurator UX step 6).
   */
  /**
   * The station location: typed on the Station step (create) or the stored row (edit —
   * this wizard cannot change it). Null until it is a valid coordinate pair.
   */
  const location = useMemo((): { lat: number; lon: number } | null => {
    const la = isEdit ? station?.lat : lat.trim() ? Number(lat) : NaN;
    const lo = isEdit ? station?.lon : lon.trim() ? Number(lon) : NaN;
    if (la == null || lo == null || !Number.isFinite(la) || !Number.isFinite(lo)) return null;
    if (la < -90 || la > 90 || lo < -180 || lo > 180) return null;
    return { lat: la, lon: lo };
  }, [isEdit, station, lat, lon]);
  const locationKey = location ? `${location.lat},${location.lon}` : "";

  /** Default zone for local time: the location's zone; the browser's only when that is null. */
  const [locationTz, setLocationTz] = useState<string | null>(null);
  const [tzNotice, setTzNotice] = useState<string | null>(null);
  const defaultTz = locationTz ?? browserTimeZone();
  const prevDefaultTz = useRef<string | null>(defaultTz);

  const jsonVerified =
    needsOptions && Boolean(testResult?.ok) && testedKey === currentJsonKey && mapping != null;

  /**
   * Catalogue load, retryable. Everything per-format depends on it, so a failure is not a
   * Source-step detail: in edit mode the wizard opens on Connection and a failed catalogue
   * leaves `provider` null, which every validator reads as "no format chosen". The error is
   * therefore rendered on every step, with a Retry.
   */
  const loadCatalogue = useCallback(async () => {
    setLoadingCatalogue(true);
    setCatalogueError(null);
    try {
      const list = await PushStationService.listPullProviders();
      if (!mounted.current) return;
      setProviders(list);
      setCatalogueError(
        list.length === 0 ? "The backend returned no pull formats — nothing can be registered." : null
      );
    } catch (err) {
      if (mounted.current) setCatalogueError(getErrorMessage(err, "loading pull formats"));
    } finally {
      if (mounted.current) setLoadingCatalogue(false);
    }
  }, []);

  useEffect(() => {
    void loadCatalogue();
  }, [loadCatalogue]);

  /**
   * Prefill the interval from the catalogue default once the format is known, but never
   * overwrite a value the operator typed (or an existing station's stored interval).
   */
  useEffect(() => {
    if (provider && !intervalTouched) {
      setIntervalS(String(provider.defaultIntervalS));
    }
  }, [provider, intervalTouched]);

  const setContactField = (field: keyof ContactFields, value: string) =>
    setContact((c) => ({ ...c, [field]: value }));

  const pickProvider = (id: string) => {
    // The sample belongs to the format it was fetched for; the invalidation effect drops
    // it as soon as providerId changes, so it is not cleared by hand here.
    setProviderId(id);
    setFieldError(null);
    if (!isEdit) {
      setIntervalTouched(false);
    }
  };

  const runTest = useCallback(async () => {
    const err = validateConnection({ provider, url, externalId, token, tokenConfigured, isEdit, storedProviderId: providerId });
    if (err) {
      setFieldError(err);
      return;
    }
    setFieldError(null);
    setTesting(true);
    const seq = ++testSeq.current;
    /** A response is only allowed to touch state while it is still the newest one. */
    const isCurrent = () => mounted.current && testSeq.current === seq;
    try {
      const trimmedToken = token.trim();
      const result = await PushStationService.testPull({
        provider: provider!.id,
        url: url.trim(),
        externalId: provider!.needsExternalId ? externalId.trim() : null,
        // The token key is OMITTED when blank — never null, never "". Combined with
        // stationRef that is what lets the backend test an existing station against its
        // stored token, which is the only way to test an unchanged authenticated config.
        ...(trimmedToken ? { token: trimmedToken } : {}),
        ...(isEdit ? { stationRef: station!.prefix ?? station!.stationId } : {}),
      });
      if (!isCurrent()) return;
      setTestResult(result);
      setTestedKey(`${url.trim()}|`);
      // Coordinates the file reports are OFFERED on the Connection step ("Use coordinates
      // from file"), never written silently over the location the operator entered.
    } catch (err2) {
      // Only a broken call to OUR backend lands here; a bad upstream is ok:false.
      if (!isCurrent()) return;
      setTestResult({ ok: false, error: getErrorMessage(err2, "testing the poll URL") });
    } finally {
      // Clear the spinner for the newest request only. A superseded one leaves it alone
      // because either a newer test owns it, or the invalidation effect below already
      // released it — otherwise editing the URL mid-test would strand the button on
      // "Testing…" forever.
      if (mounted.current && testSeq.current === seq) setTesting(false);
    }
  }, [provider, providerId, url, externalId, token, tokenConfigured, isEdit, station]);

  /**
   * Server test for the JSON configurator. `options` undefined = "Fetch" (the backend
   * returns a suggested mapping). Same stale-response guards as runTest; resolves null when
   * the response was superseded so the configurator does not act on it.
   */
  const runJsonTest = useCallback(
    async (options: JsonPullMapping | undefined): Promise<PullTestResult | null> => {
      if (!provider) return null;
      if (!url.trim().startsWith("https://")) {
        setFieldError("Poll URL must be https.");
        return null;
      }
      setFieldError(null);
      setTesting(true);
      const seq = ++testSeq.current;
      const testedUrl = url.trim();
      const requestKey = `${testedUrl}|${options ? mappingKey(options) : ""}`;
      if (options) inFlightKey.current = requestKey;
      /** Newest request, still mounted, and still describing what is on screen. */
      const isCurrent = () =>
        mounted.current &&
        testSeq.current === seq &&
        (options
          ? requestKey === currentKeyRef.current
          : currentKeyRef.current.startsWith(`${testedUrl}|`));
      try {
        const result = await PushStationService.testPull({
          provider: provider.id,
          url: testedUrl,
          ...(options ? { options } : {}),
          // Lets the backend fill suggested.time.tz from the station's location.
          ...(location ? { lat: location.lat, lon: location.lon } : {}),
          ...(isEdit ? { stationRef: station!.prefix ?? station!.stationId } : {}),
        });
        if (!isCurrent()) return null;
        setTestResult(result);
        setTestedKey(requestKey);
        return result;
      } catch (err2) {
        if (!isCurrent()) return null;
        const failed: PullTestResult = { ok: false, error: getErrorMessage(err2, "testing the poll URL") };
        setTestResult(failed);
        setTestedKey(requestKey);
        return failed;
      } finally {
        if (inFlightKey.current === requestKey) inFlightKey.current = null;
        if (mounted.current && testSeq.current === seq) setTesting(false);
      }
    },
    [provider, url, isEdit, station, location]
  );

  /** Null when the mapping step is complete (json formats only). */
  const validateJsonStep = (): string | null => {
    if (!needsOptions) return null;
    if (!mapping) return "Fetch the URL (or pick a preset) to build the field mapping.";
    const errs = validateMapping(normalizeMapping(mapping));
    if (errs.length) return `Mapping: ${errs[0]}`;
    if (!jsonVerified) return "Wait for a passing server test of the current URL and mapping before continuing.";
    return null;
  };

  /**
   * Any change to what was tested invalidates the result — including a change made while a
   * request is in flight, which `testSeq` then also stops from landing. Centralised here so
   * a new input cannot forget its own `setTestResult(null)` the way each one had to before.
   */
  useEffect(() => {
    testSeq.current += 1;
    setTestResult(null);
    setTestedKey(null);
    // The in-flight request (if any) is now orphaned and will not clear this itself.
    setTesting(false);
    inFlightKey.current = null;
  }, [url, providerId, externalId, token]);

  /**
   * JSON auto-test: whenever the URL or the mapping changes and the mapping is complete,
   * re-run the server test after a short debounce (typing in a path field must not fire
   * per keystroke). An identical url + mapping is never auto-re-tested — a failure stays
   * until something changes or the operator clicks "Test again".
   */
  const runJsonTestRef = useRef(runJsonTest);
  runJsonTestRef.current = runJsonTest;
  useEffect(() => {
    if (!needsOptions || step !== "connection" || !mapping) return;
    if (!url.trim().startsWith("https://")) return;
    const normalized = normalizeMapping(mapping);
    if (validateMapping(normalized).length) return;
    if (testedKey === currentJsonKey || inFlightKey.current === currentJsonKey) return;
    setAutoPending(true);
    const t = window.setTimeout(() => {
      setAutoPending(false);
      void runJsonTestRef.current(normalized);
    }, JSON_AUTO_TEST_DELAY_MS);
    return () => {
      window.clearTimeout(t);
      setAutoPending(false);
    };
    // currentJsonKey stands in for url + mapping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJsonKey, testedKey, needsOptions, step]);

  const jsonStatus = computeJsonTestStatus({
    url,
    mapping,
    busy: autoPending || (testing && inFlightKey.current === currentJsonKey),
    currentResult: testedKey === currentJsonKey ? testResult : null,
  });
  const jsonDisplayTz =
    mapping?.time.mode === "local" && mapping.time.tz ? mapping.time.tz : defaultTz || "UTC";

  /**
   * Resolve the location's zone once the operator is past the Station step (create) or on
   * open (edit). Only then — typing coordinates digit by digit must not fire a lookup per
   * keystroke. A failed / empty lookup falls back to the browser zone.
   */
  const pastStation = step === "connection" || step === "review";
  useEffect(() => {
    if (!location || !pastStation) return;
    let live = true;
    PushStationService.lookupTimezone(location.lat, location.lon)
      .then((tz) => {
        if (live && mounted.current) setLocationTz(tz);
      })
      .catch(() => {
        if (live && mounted.current) setLocationTz(null);
      });
    return () => {
      live = false;
    };
    // locationKey stands in for `location` (a new object every render of the same point).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey, pastStation]);

  /**
   * The default zone moved (new location, or the lookup landed). A mapping whose zone is
   * still the PREVIOUS auto-default follows it — the mapping key changes, so the passing
   * test no longer counts and must be re-run. A zone picked by hand is never touched.
   */
  // Read the mapping through a ref rather than a setMapping updater: React may re-run an
  // updater, and a setTzNotice inside one re-raised the notice after the re-test cleared it.
  const mappingRef = useRef(mapping);
  mappingRef.current = mapping;
  useEffect(() => {
    const prev = prevDefaultTz.current;
    prevDefaultTz.current = defaultTz;
    if (prev === defaultTz || !defaultTz) return;
    const m = mappingRef.current;
    if (!m || m.time.mode !== "local") return;
    if (m.time.tz && m.time.tz !== prev) return;
    const next = cloneMapping(m);
    next.time.tz = defaultTz;
    setMapping(next);
    setTzNotice(
      `Time zone updated to ${defaultTz} from the station location — the mapping is re-tested automatically.`
    );
  }, [defaultTz]);

  // A passing re-test settles the zone change the notice asked about.
  useEffect(() => {
    if (jsonVerified) setTzNotice(null);
  }, [jsonVerified]);

  const goNext = () => {
    setFieldError(null);
    if (step === "source") {
      if (!provider) {
        setFieldError("Choose a source format to continue.");
        return;
      }
      setStep("station");
      return;
    }
    if (step === "connection") {
      const err =
        validateConnection({ provider, url, externalId, token, tokenConfigured, isEdit, storedProviderId: providerId }) ??
        validateInterval(intervalS, provider) ??
        validateJsonStep();
      if (err) {
        setFieldError(err);
        return;
      }
      setStep("review");
      return;
    }
    if (step === "station") {
      const err = validateStation();
      if (err) {
        setFieldError(err);
        return;
      }
      setStep("connection");
    }
  };

  const goBack = () => {
    setFieldError(null);
    const i = steps.indexOf(step);
    if (i > 0) setStep(steps[i - 1]);
  };

  /** Create-only: in edit mode the identity is the existing row's and this wizard never
   *  touches it, so there is nothing here to validate (or to accidentally drop). */
  const validateStation = (): string | null => {
    if (isEdit) return null;
    if (!stationId.trim()) return "Station id is required.";
    if (!name.trim()) return "Display name is required.";
    const latNum = Number(lat);
    const lonNum = Number(lon);
    if (!lat.trim() || !Number.isFinite(latNum) || latNum < -90 || latNum > 90) {
      return "Latitude must be a number between -90 and 90.";
    }
    if (!lon.trim() || !Number.isFinite(lonNum) || lonNum < -180 || lonNum > 180) {
      return "Longitude must be a number between -180 and 180.";
    }
    return null;
  };

  /**
   * The test is advisory (decision 5): an upstream can legitimately be down while ops
   * registers a station. It must not be a silent skip either — an untested or failing URL
   * is the single most common cause of a station that never produces a row, so make the
   * operator say so out loud.
   */
  const confirmDespiteTest = (): boolean => {
    // JSON: no override — validateJsonStep already refused an unverified mapping.
    if (needsOptions) return jsonVerified;
    if (testResult?.ok) return true;
    const reason = testResult
      ? `The connection test FAILED:\n\n${testResult.error || "no usable data"}`
      : "The connection has not been tested.";
    return window.confirm(
      `${reason}\n\nSave anyway?\n\nThe station will be registered and polled on its interval; ` +
        `if the URL is wrong it will simply never produce an observation.`
    );
  };

  const buildPull = (): UpdatePullRequest => ({
    pollUrl: url.trim(),
    pollProvider: provider!.id,
    pollIntervalS: Number(intervalS.trim()),
    pollExternalId: provider!.needsExternalId ? externalId.trim() || undefined : undefined,
    // Omitted when blank so the backend keeps the stored token (it cannot be echoed back,
    // so "empty" must never be read as "clear").
    pollAuthToken: token.trim() || undefined,
    // Required for json, and must be absent for every other provider (backend rejects it).
    pollOptions: needsOptions && mapping ? normalizeMapping(mapping) : undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const connErr =
      validateConnection({ provider, url, externalId, token, tokenConfigured, isEdit, storedProviderId: providerId }) ??
      validateInterval(intervalS, provider) ??
      validateJsonStep() ??
      validateStation();
    if (connErr) {
      setFieldError(connErr);
      return;
    }
    if (!confirmDespiteTest()) return;
    setFieldError(null);
    setSaving(true);
    try {
      if (isEdit) {
        await PushStationService.updatePull(station!.prefix ?? station!.stationId, buildPull());
        toast.success(`Pull config saved for ${station!.stationId}`);
        onSaved("edit");
      } else {
        const req: CreatePushStationRequest = {
          stationId: stationId.trim(),
          name: name.trim(),
          lat: Number(lat),
          lon: Number(lon),
          credentialMode: "pull",
          contact: contactFromFields(contact),
          pull: buildPull(),
        };
        const result = await PushStationService.create(req);
        if (result.contactSaved === false) {
          toast.warning(
            "Station created, but the registry row failed to save — open Settings and save once to retry"
          );
        }
        onSaved("create");
      }
    } catch (err) {
      toast.error(getErrorMessage(err, isEdit ? "saving pull config" : "registering pull station"));
    } finally {
      setSaving(false);
    }
  };

  const isLast = step === "review";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <form
        onSubmit={handleSubmit}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-gray-800 p-6 shadow-xl space-y-4"
      >
        <div>
          <h2 className="text-lg font-semibold text-white">
            {isEdit ? `Pull config — ${station!.stationId}` : "Register pull station"}
          </h2>
          <p className="mt-1 text-sm text-white75">
            A pull station has NO push credential — we fetch it from a URL on the interval
            below. Only configure this when the owner has agreed AND the source&apos;s own
            terms permit it.
          </p>
        </div>

        <StepHeader steps={steps} current={step} />

        {/* Catalogue status belongs to the whole wizard, not the Source step: edit mode
            opens on Connection, where a failed catalogue is exactly as fatal (no provider
            => no per-format fields, no interval bounds, no valid save) but used to be
            invisible. Retry is here because a transient 5xx should not mean re-opening
            the dialog and losing everything typed. */}
        {loadingCatalogue && (
          <p data-testid="catalogue-loading" className="text-sm text-white75">
            Loading pull formats…
          </p>
        )}
        {!loadingCatalogue && catalogueError && (
          <div
            data-testid="catalogue-error"
            className="rounded border border-red-700 bg-red-900/30 p-3 text-sm text-red-200"
          >
            <p className="break-words">{catalogueError}</p>
            <button
              type="button"
              onClick={() => void loadCatalogue()}
              className="mt-2 rounded bg-gray-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-600"
            >
              Retry
            </button>
          </div>
        )}

        {step === "source" && (
          <div>
            <p className="mb-2 text-sm font-semibold text-white">Which format does the source serve?</p>
            {!loadingCatalogue && !catalogueError && (
              <div className="space-y-2">
                {providers.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickProvider(p.id)}
                    aria-pressed={providerId === p.id}
                    className={`block w-full rounded border p-3 text-left ${
                      providerId === p.id
                        ? "border-accent bg-gray-700"
                        : "border-gray-600 bg-gray-700/30 hover:bg-gray-700/60"
                    }`}
                  >
                    <span className="block text-sm font-medium text-white">{p.label}</span>
                    <span className="mt-1 block text-xs text-white75">{p.description}</span>
                    <span className="mt-1 block text-xs text-white50">
                      {p.needsAuthToken ? "needs an owner token" : "open — no token"}
                      {p.needsExternalId ? ` · needs ${p.externalIdLabel ?? "an external id"}` : ""}
                      {` · polls every ${p.defaultIntervalS}s by default`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === "connection" && (
          <div className="space-y-3">
            {isEdit && (
              <div>
                <label className={labelCls} htmlFor="pull-format">
                  Format
                </label>
                <select
                  id="pull-format"
                  className={selectCls}
                  value={providerId}
                  onChange={(e) => pickProvider(e.target.value)}
                >
                  <option value="">—</option>
                  {/* The station's stored provider must stay selected even before the
                      catalogue arrives (and even if the backend later drops that format) —
                      without this option the select silently resets to "—" on first paint
                      and an edit would clear the provider the operator never touched. */}
                  {providerId && !providers.some((p) => p.id === providerId) && (
                    <option value={providerId}>{providerId}</option>
                  )}
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!isEdit && provider && (
              <p className="text-sm text-white75">
                Format: <span className="text-white">{provider.label}</span>
              </p>
            )}
            <div>
              <label className={labelCls} htmlFor="pull-url">
                Poll URL (https)
              </label>
              <input
                id="pull-url"
                className={inputCls}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={provider?.urlPlaceholder ?? "https://…"}
              />
            </div>
            {provider?.needsExternalId && (
              <div>
                <label className={labelCls} htmlFor="pull-external-id">
                  {provider.externalIdLabel ?? "Provider station id"}
                </label>
                <input
                  id="pull-external-id"
                  className={inputCls}
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                  placeholder="12345"
                />
                <p className="mt-1 text-xs text-white75">
                  The provider&apos;s own id, not the Surfr station id.
                </p>
              </div>
            )}
            {provider?.needsAuthToken && (
              <div>
                <label className={labelCls} htmlFor="pull-token">
                  Owner access token{" "}
                  {tokenConfigured && (
                    <span className="text-green-300">· configured — leave empty to keep</span>
                  )}
                </label>
                <input
                  id="pull-token"
                  className={inputCls}
                  type="password"
                  autoComplete="off"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder={
                    tokenConfigured ? "leave empty to keep the stored token" : "paste the owner's token"
                  }
                />
                <p className="mt-1 text-xs text-white75">
                  Write-only: it is never shown again, here or anywhere.
                </p>
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="pull-interval">
                Interval (s) — {provider?.minIntervalS ?? 60}–{MAX_POLL_INTERVAL_S}
              </label>
              <input
                id="pull-interval"
                className={inputCls}
                type="number"
                min={provider?.minIntervalS ?? 60}
                max={MAX_POLL_INTERVAL_S}
                value={intervalS}
                onChange={(e) => {
                  setIntervalTouched(true);
                  setIntervalS(e.target.value);
                }}
              />
              {provider && (
                <p className="mt-1 text-xs text-white75">
                  {provider.label} updates roughly every {provider.defaultIntervalS}s — polling
                  faster than the source refreshes only costs requests.
                </p>
              )}
            </div>
            {needsOptions ? (
              <div>
                <p className="mb-1 text-sm font-semibold text-white">Field mapping</p>
                <p className="mb-2 text-xs text-white75">
                  Tell us which JSON fields hold wind, gust, direction and time. Saving needs a
                  passing server test with exactly this URL and mapping.
                </p>
                {/* Hidden the moment the re-test passes (same render), not one effect later. */}
                {tzNotice && !jsonVerified && (
                  <p data-testid="tz-notice" className="mb-2 text-xs text-amber-300">
                    {tzNotice}
                  </p>
                )}
                <JsonMappingConfigurator
                  url={url}
                  mapping={mapping}
                  onMappingChange={setMapping}
                  onTest={runJsonTest}
                  testing={testing}
                  result={testResult}
                  verified={jsonVerified}
                  defaultOptions={provider?.defaultOptions ?? null}
                  defaultTz={defaultTz}
                  autoRunOnMount={isEdit}
                />
              </div>
            ) : (
              <>
                <div>
                  <button
                    type="button"
                    onClick={() => void runTest()}
                    disabled={testing || !provider}
                    className="rounded bg-gray-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-600 disabled:opacity-50"
                  >
                    {testing ? "Testing…" : "Test connection"}
                  </button>
                  <span className="ml-2 text-xs text-white75">
                    One fetch, nothing is saved. Advisory — you can register without it.
                  </span>
                </div>
                {testResult && <TestResultPanel result={testResult} />}
                {testResult?.ok &&
                  testResult.sample?.lat != null &&
                  testResult.sample?.lon != null &&
                  (location == null ||
                    Math.abs(location.lat - testResult.sample.lat) > 1e-6 ||
                    Math.abs(location.lon - testResult.sample.lon) > 1e-6) && (
                    <div
                      data-testid="file-coords-offer"
                      className="rounded border border-gray-600 bg-gray-700/40 p-3 text-sm text-white75"
                    >
                      <p>
                        The file reports{" "}
                        <span className="text-white">
                          {testResult.sample.lat}, {testResult.sample.lon}
                        </span>
                        {location ? (
                          <>
                            {" "}
                            — you entered {location.lat}, {location.lon}.
                          </>
                        ) : null}
                      </p>
                      {isEdit ? (
                        <p className="mt-1 text-xs">
                          This wizard saves only the pull config — change the position in the
                          station&apos;s Settings if the file is right.
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setLat(String(testResult.sample!.lat));
                            setLon(String(testResult.sample!.lon));
                          }}
                          className="mt-2 rounded bg-gray-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-600"
                        >
                          Use coordinates from file
                        </button>
                      )}
                    </div>
                  )}
              </>
            )}
          </div>
        )}

        {step === "station" && !isEdit && (
          <div className="space-y-3">
            <div>
              <label className={labelCls} htmlFor="pull-station-id">
                Surfr station id (public, lowercase-dashes)
              </label>
              <input
                id="pull-station-id"
                className={`${inputCls} ${isEdit ? "cursor-not-allowed opacity-60" : ""}`}
                value={stationId}
                onChange={(e) => setStationId(e.target.value)}
                placeholder="cumbuco-oceanview"
                readOnly={isEdit}
                disabled={isEdit}
              />
              {isEdit && (
                <p className="mt-1 text-xs text-white75">
                  Immutable — it keys the public identity and every stored observation.
                </p>
              )}
            </div>
            <div>
              <label className={labelCls} htmlFor="pull-name">
                Display name
              </label>
              <input
                id="pull-name"
                className={inputCls}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Cumbuco — Ocean View"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls} htmlFor="pull-lat">
                  Latitude
                </label>
                <input
                  id="pull-lat"
                  className={inputCls}
                  type="number"
                  step="any"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="pull-lon">
                  Longitude
                </label>
                <input
                  id="pull-lon"
                  className={inputCls}
                  type="number"
                  step="any"
                  value={lon}
                  onChange={(e) => setLon(e.target.value)}
                />
              </div>
            </div>
            {/* Same state as the two fields: a click / drag fills them, typing moves the
                pin, and "Use coordinates from file" lands here too. The tz-follow and the
                "re-test" notice key off `location`, so they fire however it changed. */}
            <LocationPickerMap
              lat={location?.lat ?? null}
              lon={location?.lon ?? null}
              onPick={(la, lo) => {
                setLat(String(la));
                setLon(String(lo));
              }}
              heightPx={240}
            />
            <p className="text-xs text-white75">
              The position comes first so a JSON source&apos;s local time zone can default
              from it. If the source file reports its own coordinates, the next step offers
              them — nothing is overwritten silently.
            </p>
            {!isEdit && (
              <div className="border-t border-gray-700 pt-3">
                <p className="mb-2 text-sm font-medium text-white">
                  Owner contact &amp; station details (optional — ops metadata, never public)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Contact person</label>
                    <input
                      className={inputCls}
                      value={contact.contactName}
                      onChange={(e) => setContactField("contactName", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Organization / school</label>
                    <input
                      className={inputCls}
                      value={contact.organization}
                      onChange={(e) => setContactField("organization", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Organization type</label>
                    <select
                      className={selectCls}
                      value={contact.organizationType}
                      onChange={(e) => setContactField("organizationType", e.target.value)}
                    >
                      <option value="">—</option>
                      {ORGANIZATION_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input
                      className={inputCls}
                      type="email"
                      value={contact.email}
                      onChange={(e) => setContactField("email", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input
                      className={inputCls}
                      value={contact.phone}
                      onChange={(e) => setContactField("phone", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Website</label>
                    <input
                      className={inputCls}
                      value={contact.website}
                      onChange={(e) => setContactField("website", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Placement type</label>
                    <select
                      className={selectCls}
                      value={contact.stationType}
                      onChange={(e) => setContactField("stationType", e.target.value)}
                    >
                      <option value="">—</option>
                      {STATION_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Surfr spot id</label>
                    <input
                      className={inputCls}
                      type="number"
                      value={contact.spotId}
                      onChange={(e) => setContactField("spotId", e.target.value)}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className={labelCls}>Notes</label>
                  <textarea
                    className={inputCls}
                    rows={2}
                    value={contact.notes}
                    onChange={(e) => setContactField("notes", e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {step === "review" && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-white">Review</p>
            <dl data-testid="pull-review" className="rounded border border-gray-700 bg-gray-700/30 p-3 text-sm">
              {[
                ["Format", provider?.label ?? providerId],
                ["Poll URL", url.trim()],
                ...(provider?.needsExternalId
                  ? [[provider.externalIdLabel ?? "Provider station id", externalId.trim()] as const]
                  : []),
                ...(provider?.needsAuthToken
                  ? [
                      [
                        "Owner token",
                        token.trim()
                          ? "new token will be stored"
                          : tokenConfigured
                            ? "configured — kept unchanged"
                            : "none",
                      ] as const,
                    ]
                  : []),
                ...(needsOptions ? [["Field mapping", summarizeMapping(mapping)] as const] : []),
                ["Interval", `${intervalS.trim()}s`],
                // In edit mode the identity is NOT part of this save — it is shown straight
                // from the existing row so the operator can confirm which station they are
                // reconfiguring, and it is labelled as unchanged rather than looking like a
                // field this wizard writes.
                ["Station id", isEdit ? station!.stationId : stationId.trim()],
                ["Display name", isEdit ? (station!.name ?? "") : name.trim()],
                [
                  "Position",
                  isEdit
                    ? `${station!.lat ?? "—"}, ${station!.lon ?? "—"}`
                    : `${lat.trim()}, ${lon.trim()}`,
                ],
                [
                  "Connection test",
                  needsOptions
                    ? jsonVerified
                      ? "passed (this URL + mapping)"
                      : "not passed for the current mapping"
                    : testResult
                      ? testResult.ok
                        ? "passed"
                        : `failed — ${describeErrorCode(testResult.error) || ""}`
                      : "not run",
                ],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-0.5">
                  <dt className="text-white75">{k}</dt>
                  <dd className="break-all text-right text-white">{v || "—"}</dd>
                </div>
              ))}
            </dl>
            {isEdit && (
              <p className="text-xs text-white75">
                Station id, display name and position are shown for reference only — this
                wizard saves the pull config. Edit them in the station&apos;s Settings.
              </p>
            )}
            {needsOptions && !jsonVerified && (
              <p className="text-sm text-red-300">
                The mapping has no passing server test — go Back and Re-test before saving.
              </p>
            )}
            {!needsOptions && !testResult?.ok && (
              <p className="text-sm text-amber-300">
                {testResult
                  ? "The connection test failed — saving asks for a confirmation."
                  : "The connection was never tested — saving asks for a confirmation."}
              </p>
            )}
            <p className="text-xs text-white75">
              {isEdit
                ? "The station keeps its id, name, position, status and history; only the pull config changes."
                : "The station becomes publicly visible on the live wind map after its first successful fetch — registration is the approval."}
            </p>
          </div>
        )}

        {fieldError && <p className="text-sm text-red-300">{fieldError}</p>}

        <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 border-t border-gray-700 bg-gray-800 px-6 py-4">
        {/* The JSON test verdict sits next to Next/Save, always for the CURRENT url +
            mapping, so nobody has to guess whether a re-test is needed. */}
        {needsOptions && step === "connection" && (
          <div className="mb-3">
            <JsonTestStatus
              status={jsonStatus}
              result={testedKey === currentJsonKey ? testResult : null}
              displayTz={jsonDisplayTz}
              canTestAgain={!testing && !autoPending}
              onTestAgain={() => {
                if (mapping) void runJsonTest(normalizeMapping(mapping));
              }}
            />
          </div>
        )}
        <div className="flex justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-4 py-2 text-white75 hover:text-white"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            {steps.indexOf(step) > 0 && (
              <button
                type="button"
                onClick={goBack}
                className="rounded bg-gray-700 px-4 py-2 text-white hover:bg-gray-600"
              >
                Back
              </button>
            )}
            {isLast ? (
              <button
                type="submit"
                disabled={saving || (needsOptions && !jsonVerified)}
                className="rounded bg-accent px-4 py-2 font-medium text-white disabled:opacity-50"
              >
                {saving ? "Saving…" : isEdit ? "Save pull config" : "Register station"}
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                disabled={needsOptions && step === "connection" && jsonStatus.kind !== "passed"}
                title={
                  needsOptions && step === "connection" && jsonStatus.kind !== "passed"
                    ? "Waiting for a passing test of this mapping"
                    : undefined
                }
                className="rounded bg-accent px-4 py-2 font-medium text-white disabled:opacity-50"
              >
                Next
              </button>
            )}
          </div>
        </div>
        </div>
      </form>
    </div>
  );
};

export default PullStationWizard;
